/**
 * v132 — In-memory migration from v1/v2 sessions to v3.
 *
 * Why in-memory: bulk-rewriting persisted history on first load is high-risk
 * for a feature this fresh. Instead, on read we hand the UI a v3-shaped
 * session (one implicit Set wrapping the flat richShots[]) — on next save
 * the v3 shape persists. v1/v2 records that the user never touches stay
 * in their original shape on disk, which preserves a clean rollback path.
 */

import type { ShotSession } from './types';
import type { ShotSet } from './v3-schema';

function genSetId(): string {
  return `set_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Returns a v3-shaped clone of the session. If `sets` already exists, returns
 * the session unchanged. Otherwise wraps the flat `richShots[]` in a single
 * implicit Set so the UI can render a uniform shape.
 *
 * Idempotent: calling on an already-v3 session is a no-op.
 */
export function migrateSessionToV3(session: ShotSession): ShotSession {
  if (session.sets && session.sets.length > 0) return session;

  const richShots = session.richShots ?? [];
  if (richShots.length === 0) {
    // Aggregate-only quick-log session (no richShots). Don't fabricate a set —
    // there's nothing to put in it.
    return session;
  }

  const implicitSet: ShotSet = {
    id: genSetId(),
    label: 'Set 1',
    startedAt: session.loggedAt,
    endedAt: session.endedAt,
    shotIds: richShots.map((s) => s.id),
  };

  return {
    ...session,
    sets: [implicitSet],
    schemaVersion: 3,
  };
}

/**
 * Migrate an array of sessions. Same idempotency contract.
 */
export function migrateAllToV3(sessions: readonly ShotSession[]): ShotSession[] {
  return sessions.map(migrateSessionToV3);
}
